import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Lottie from "lottie-react";
import "./../Styles/MESection.css";
import successAnimation from "./../ani/celebration.json";
import { useToast } from './ToastContext';
import { useConfirm } from './ConfirmDialog';
import { FiChevronDown, FiEdit2, FiTrash2, FiPlus, FiArrowLeft, FiSearch, FiArrowRight } from 'react-icons/fi';
const apiUrl = process.env.REACT_APP_API_URL ;

/* ── Inline: Project Management (View List, Edit, Delete, Create) ─── */
const ProjectManagementInline = ({ onClose, onProjectCreated }) => {
  const [view, setView] = useState('list'); // 'list' | 'create'
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Create project state
  const [createName, setCreateName] = useState('');
  const [createDesc, setCreateDesc] = useState('');
  const [createError, setCreateError] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Edit project state
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editError, setEditError] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [expandedDescIds, setExpandedDescIds] = useState(new Set());

  const toggleDesc = (id) => {
    setExpandedDescIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const showToast = useToast();
  const confirm = useConfirm();

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${apiUrl}/api/fetch-projects`);
      if (res.ok) {
        const data = await res.json();
        setProjects(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
      showToast('Failed to load projects.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setCreateError('');
    if (!createName.trim()) {
      showToast('Project name is required.', 'warning');
      return;
    }
    if (!createDesc.trim()) {
      showToast('Project description is required.', 'warning');
      return;
    }

    try {
      setIsCreating(true);
      const response = await fetch(`${apiUrl}/api/upload-project`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_name: createName.trim(),
          description: createDesc.trim() || null
        }),
      });

      if (response.ok) {
        setCreateName('');
        setCreateDesc('');
        showToast('Project created successfully!', 'success');
        if (onProjectCreated) onProjectCreated();
        await fetchProjects();
        setView('list');
      } else if (response.status === 409) {
        const data = await response.json();
        setCreateError(data.error || 'A project with this name already exists.');
      } else {
        showToast('Failed to create project. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error creating project:', error);
      showToast('An error occurred. Please try again.', 'error');
    } finally {
      setIsCreating(false);
    }
  };

  const handleStartEdit = (proj) => {
    setEditingProjectId(proj._id);
    setEditName(proj.project_name || '');
    setEditDesc(proj.description || '');
    setEditError('');
  };

  const handleCancelEdit = () => {
    setEditingProjectId(null);
    setEditName('');
    setEditDesc('');
    setEditError('');
  };

  const handleUpdateSubmit = async (e, projectId) => {
    e.preventDefault();
    setEditError('');
    if (!editName.trim()) {
      showToast('Project name is required.', 'warning');
      return;
    }
    if (!editDesc.trim()) {
      showToast('Project description is required.', 'warning');
      return;
    }

    try {
      setIsUpdating(true);
      const response = await fetch(`${apiUrl}/api/update-project/${projectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_name: editName.trim(),
          description: editDesc.trim() || null
        }),
      });

      if (response.ok) {
        showToast('Project updated successfully!', 'success');
        if (onProjectCreated) onProjectCreated();
        await fetchProjects();
        setEditingProjectId(null);
      } else if (response.status === 409) {
        const data = await response.json();
        setEditError(data.error || 'A project with this name already exists.');
      } else {
        const data = await response.json().catch(() => ({}));
        showToast(data.error || 'Failed to update project. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error updating project:', error);
      showToast('An error occurred while updating project.', 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteProject = async (proj) => {
    const ok = await confirm({
      title: 'Delete Project',
      message: `Are you sure you want to delete the project "${proj.project_name}"? All project associations will be removed.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'danger'
    });
    if (!ok) return;

    try {
      const response = await fetch(`${apiUrl}/api/delete-project/${proj._id}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        showToast(`Project "${proj.project_name}" deleted successfully.`, 'success');
        if (onProjectCreated) onProjectCreated();
        await fetchProjects();
      } else {
        const data = await response.json().catch(() => ({}));
        showToast(data.error || 'Failed to delete project.', 'error');
      }
    } catch (err) {
      console.error('Error deleting project:', err);
      showToast('An error occurred while deleting project.', 'error');
    }
  };

  const filteredProjects = projects.filter(p => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const nameMatch = p.project_name && p.project_name.toLowerCase().includes(term);
    const descMatch = p.description && p.description.toLowerCase().includes(term);
    return nameMatch || descMatch;
  });

  if (view === 'create') {
    return (
      <div className="pmodal-section">
        <div className="pmodal-header" style={{ paddingRight: '60px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div className="pmodal-header-icon purple-icon">📁</div>
            <div className="pmodal-header-text">
              <h2>Create a New Project</h2>
              <p>Add and create a new project.</p>
            </div>
          </div>
        </div>
        <div className="pmodal-divider" />
        <div className="pmodal-body">
          <div style={{ marginBottom: '14px' }}>
            <button
              type="button"
              className="pmodal-btn-cancel"
              onClick={() => { setView('list'); setCreateError(''); }}
              style={{ fontSize: '12px', padding: '6px 14px', display: 'inline-flex', alignItems: 'center', gap: '5px' }}
            >
              <FiArrowLeft size={13} />
              Back to Projects List
            </button>
          </div>
          <form onSubmit={handleCreateSubmit}>
            <div className="form-group">
              <label className="pmodal-label">Project Name <span className="required-star">*</span></label>
              <input
                type="text"
                className="pmodal-input"
                placeholder="Enter project name"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="form-group">
              <label className="pmodal-label">Description <span className="required-star">*</span></label>
              <textarea
                className="pmodal-input pmodal-textarea"
                placeholder="Enter project description"
                value={createDesc}
                onChange={(e) => setCreateDesc(e.target.value)}
                rows={3}
                required
              />
            </div>
            {createError && <p className="pmodal-helper" style={{ color: '#ef4444', marginTop: '6px' }}>{createError}</p>}
            <div className="pmodal-footer" style={{ marginTop: '16px', padding: '16px 0 8px 0', borderTop: '1px solid var(--border)' }}>
              <button
                type="button"
                className="pmodal-btn-cancel"
                onClick={() => { setView('list'); setCreateError(''); }}
                disabled={isCreating}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="pmodal-btn pmodal-btn-orange"
                disabled={isCreating}
              >
                {isCreating ? 'Creating...' : 'Create Project'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="pmodal-section">
      <div className="pmodal-header" style={{ paddingRight: '60px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div className="pmodal-header-icon purple-icon">📁</div>
          <div className="pmodal-header-text">
            <h2>Project Management</h2>
            <p>Manage existing projects, edit details, or create a new project.</p>
          </div>
        </div>
      </div>

      <div className="pmodal-divider" />

      <div className="pmodal-body" style={{ paddingBottom: '16px' }}>
        {/* Action Toolbar: Search on Left, Create Button on Right (No overlap with modal close X) */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: '220px' }}>
            <FiSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: '14px' }} />
            <input
              type="text"
              className="pmodal-input"
              style={{ paddingLeft: '34px', fontSize: '13px' }}
              placeholder="Search projects by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="pmodal-btn pmodal-btn-orange"
            onClick={() => { setView('create'); setCreateError(''); }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', padding: '9px 18px', fontWeight: '600', whiteSpace: 'nowrap' }}
          >
            <FiPlus size={15} />
            Create Project
          </button>
        </div>

        {/* Projects Tabular View */}
        <div className="project-mgmt-table-wrapper" style={{ maxHeight: '380px', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: '36px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13.5px' }}>
              Loading projects...
            </div>
          ) : filteredProjects.length === 0 ? (
            <div style={{ padding: '36px 20px', textAlign: 'center', backgroundColor: 'var(--bg-subtle, #f9fafb)' }}>
              <p style={{ margin: '0 0 12px 0', color: 'var(--text-secondary)', fontSize: '14px', fontWeight: '500' }}>
                {searchTerm ? 'No projects match your search.' : 'No projects found.'}
              </p>
              <button
                type="button"
                className="pmodal-btn pmodal-btn-orange"
                onClick={() => setView('create')}
                style={{ fontSize: '12.5px', padding: '7px 16px' }}
              >
                + Create Project
              </button>
            </div>
          ) : (
            <table className="project-mgmt-table">
              <thead>
                <tr>
                  <th style={{ width: '28%' }}>
                    Project {editingProjectId ? <span className="required-star">*</span> : null}
                  </th>
                  <th style={{ width: '46%' }}>
                    Description {editingProjectId ? <span className="required-star">*</span> : null}
                  </th>
                  <th style={{ width: '26%', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.map((proj) => {
                  const isEditing = editingProjectId === proj._id;

                  if (isEditing) {
                    return (
                      <tr key={proj._id} style={{ backgroundColor: 'var(--brand-light, rgba(234, 88, 12, 0.05))' }}>
                        <td style={{ verticalAlign: 'top', padding: '10px 12px' }}>
                          <label className="pmodal-label" style={{ fontSize: '11.5px', marginBottom: '4px', display: 'block', fontWeight: '600' }}>
                            Project <span className="required-star">*</span>
                          </label>
                          <input
                            type="text"
                            className="pmodal-input"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            placeholder="Project name *"
                            style={{ fontSize: '13px', padding: '6px 10px', width: '100%', boxSizing: 'border-box' }}
                            required
                            autoFocus
                          />
                          {editError && (
                            <p style={{ color: '#ef4444', fontSize: '11.5px', margin: '4px 0 0 0' }}>
                              {editError}
                            </p>
                          )}
                        </td>
                        <td style={{ verticalAlign: 'top', padding: '10px 12px' }}>
                          <label className="pmodal-label" style={{ fontSize: '11.5px', marginBottom: '4px', display: 'block', fontWeight: '600' }}>
                            Description <span className="required-star">*</span>
                          </label>
                          <textarea
                            className="pmodal-input pmodal-textarea"
                            value={editDesc}
                            onChange={(e) => setEditDesc(e.target.value)}
                            placeholder="Project description *"
                            rows={2}
                            style={{ fontSize: '13px', padding: '6px 10px', width: '100%', minHeight: '52px', maxHeight: '85px', resize: 'vertical', boxSizing: 'border-box' }}
                            required
                          />
                        </td>
                        <td style={{ verticalAlign: 'top', padding: '10px 12px', textAlign: 'center' }}>
                          <div style={{ display: 'inline-flex', gap: '6px', alignItems: 'center', marginTop: '22px' }}>
                            <button
                              type="button"
                              className="pmodal-btn pmodal-btn-orange"
                              onClick={(e) => handleUpdateSubmit(e, proj._id)}
                              style={{ fontSize: '12px', padding: '6px 12px' }}
                              disabled={isUpdating}
                            >
                              {isUpdating ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              type="button"
                              className="pmodal-btn-cancel"
                              onClick={handleCancelEdit}
                              style={{ fontSize: '12px', padding: '6px 12px' }}
                              disabled={isUpdating}
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  }

                  return (
                    <tr key={proj._id}>
                      <td style={{ fontWeight: '600', color: 'var(--text-primary)', wordBreak: 'break-word' }}>
                        {proj.project_name}
                      </td>
                      <td className="project-mgmt-desc-cell">
                        {!proj.description ? (
                          <span style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: '12.5px' }}>
                            No description provided
                          </span>
                        ) : (
                          <div>
                            <div
                              className={expandedDescIds.has(proj._id) ? 'project-mgmt-desc-full' : 'project-mgmt-desc-clamped'}
                              title={expandedDescIds.has(proj._id) ? '' : proj.description}
                            >
                              {proj.description}
                            </div>
                            {proj.description.length > 80 && (
                              <button
                                type="button"
                                onClick={() => toggleDesc(proj._id)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  padding: 0,
                                  marginTop: '3px',
                                  fontSize: '11px',
                                  color: 'var(--brand, #ea580c)',
                                  cursor: 'pointer',
                                  fontWeight: '600',
                                  textDecoration: 'underline'
                                }}
                              >
                                {expandedDescIds.has(proj._id) ? 'Show less' : 'Show more'}
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'inline-flex', gap: '8px', justifyContent: 'center' }}>
                          <button
                            type="button"
                            className="project-mgmt-btn-edit"
                            onClick={() => handleStartEdit(proj)}
                            title="Edit project name & description"
                          >
                            <FiEdit2 size={12} />
                            Edit
                          </button>
                          <button
                            type="button"
                            className="project-mgmt-btn-delete"
                            onClick={() => handleDeleteProject(proj)}
                            title="Delete project"
                          >
                            <FiTrash2 size={12} />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="pmodal-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>
          Total Projects: {projects.length}
        </span>
        <button type="button" className="pmodal-btn-cancel" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
};

const UploadProjectInline = ProjectManagementInline;

/* ── Inline: Assign Project ──────────────────────────────── */
const AssignProjectInline = ({ onClose, onProjectAssigned }) => {
  const [subManagers, setSubManagers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [leads, setLeads] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedManager, setSelectedManager] = useState('');
  const [selectedManagerObj, setSelectedManagerObj] = useState(null);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedProjectObj, setSelectedProjectObj] = useState(null);
  const [selectedLead, setSelectedLead] = useState('');
  const [selectedLeadObj, setSelectedLeadObj] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [managerDropdownOpen, setManagerDropdownOpen] = useState(false);
  const [leadDropdownOpen, setLeadDropdownOpen] = useState(false);
  const [employeesDropdownOpen, setEmployeesDropdownOpen] = useState(false);
  const projectRef = useRef(null);
  const managerRef = useRef(null);
  const leadRef = useRef(null);
  const employeesRef = useRef(null);
  const showToast = useToast();
  const confirm = useConfirm();

  useEffect(() => {
    fetch(`${apiUrl}/api/sub-managers`).then(r => r.json()).then(setSubManagers).catch(() => {});
    fetch(`${apiUrl}/api/fetch-projects`).then(r => r.json()).then(setProjects).catch(() => {});
    fetch(`${apiUrl}/api/all-leads`).then(r => r.json()).then(data => setLeads(Array.isArray(data) ? data : [])).catch(() => {});
    fetch(`${apiUrl}/api/sub-manager-employees`).then(r => r.json()).then(setEmployees).catch(() => {});
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (projectRef.current && !projectRef.current.contains(event.target)) setProjectDropdownOpen(false);
      if (managerRef.current && !managerRef.current.contains(event.target)) setManagerDropdownOpen(false);
      if (leadRef.current && !leadRef.current.contains(event.target)) setLeadDropdownOpen(false);
      if (employeesRef.current && !employeesRef.current.contains(event.target)) setEmployeesDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCheckboxChange = (empId) => {
    setManagerDropdownOpen(false);
    setProjectDropdownOpen(false);
    setLeadDropdownOpen(false);
    setSelectedEmployees(prev => {
      const target = String(empId).trim().toLowerCase();
      const exists = prev.some(id => String(id).trim().toLowerCase() === target);
      if (exists) {
        return prev.filter(id => String(id).trim().toLowerCase() !== target);
      } else {
        return [...prev, String(empId).trim()];
      }
    });
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!selectedProject || !selectedManager) {
      showToast('Please select both a project and a manager.', 'warning');
      return;
    }

    // Check if any selected employee is already working on a project so user can confirm
    if (selectedEmployees.length > 0) {
      const assignedTargetProjectName = selectedProjectObj?.project_name || 'this project';
      const employeesWithProjects = selectedEmployees
        .map(empId => {
          const emp = employees.find(e => String(e.employee_number || e._id).trim().toLowerCase() === String(empId).trim().toLowerCase());
          return {
            name: emp?.full_name || empId,
            currentProjects: emp?.current_projects || (emp?.current_project && emp.current_project !== 'None' ? [emp.current_project] : [])
          };
        })
        .filter(item => item.currentProjects && item.currentProjects.length > 0);

      if (employeesWithProjects.length > 0) {
        const details = employeesWithProjects
          .map(item => `• ${item.name}: currently assigned to "${item.currentProjects.join(', ')}"`)
          .join('\n');

        const ok = await confirm({
          title: 'Confirm Project Assignment',
          message: `The following employee(s) are already assigned to project(s):\n\n${details}\n\nDo you want to proceed with assigning them to "${assignedTargetProjectName}"?`,
          confirmLabel: 'Proceed',
          cancelLabel: 'Cancel',
          variant: 'info'
        });

        if (!ok) return;
      }
    }

    try {
      const response = await fetch(`${apiUrl}/api/assign-project`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: selectedProject,
          manager_id: selectedManager,
          lead_id: selectedLead,
          employee_id: selectedEmployees
        }),
      });
      const data = await response.json();
      if (response.ok) {
        showToast(data.message || 'Project assigned successfully!', 'success');
        setSelectedManager(''); setSelectedManagerObj(null);
        setSelectedProject(''); setSelectedProjectObj(null);
        setSelectedLead(''); setSelectedLeadObj(null);
        setSelectedEmployees([]);
        if (onProjectAssigned) onProjectAssigned();
        onClose();
      } else if (response.status === 409 || (data.message && data.message.toLowerCase().includes('already assigned')) || (data.error && data.error.toLowerCase().includes('already assigned'))) {
        showToast(data.message || data.error || 'Project is already assigned.', 'warning');
      } else {
        showToast(data.error || data.message || 'An error occurred. Please try again.', 'error');
      }
    } catch (error) {
      showToast('An error occurred. Please try again.', 'error');
    }
  };

  const dropdownTriggerStyle = (isOpen) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    userSelect: 'none',
    backgroundColor: 'var(--bg-input)',
    color: 'var(--text-primary)',
    borderColor: isOpen ? '#3b82f6' : 'var(--border-strong)',
    boxShadow: isOpen ? '0 0 0 3px rgba(59, 130, 246, 0.15)' : 'none',
    borderRadius: '8px',
    padding: '10px 14px'
  });

  const dropdownListStyle = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 1000,
    marginTop: '4px',
    backgroundColor: 'var(--bg-elevated, #ffffff)',
    border: '1px solid var(--border, #e5e7eb)',
    borderRadius: '8px',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.08)',
    maxHeight: '200px',
    overflowY: 'auto',
    boxSizing: 'border-box'
  };

  const dropdownItemStyle = (isSelected) => ({
    padding: '10px 16px',
    cursor: 'pointer',
    fontSize: '13.5px',
    color: isSelected ? 'var(--brand, #f59e0b)' : 'var(--text-primary)',
    fontWeight: isSelected ? '600' : '500',
    backgroundColor: isSelected ? 'var(--brand-light, #fef3c7)' : 'transparent',
    borderBottom: '1px solid var(--border-light, #f1f5f9)',
    transition: 'background-color 0.15s ease'
  });

  const chevronStyle = (isOpen) => ({
    fontSize: '1.1rem',
    color: 'var(--text-secondary)',
    transition: 'transform 0.2s ease',
    transform: isOpen ? 'rotate(180deg)' : 'none',
    flexShrink: 0
  });

  const filteredLeads = leads.filter(lead => String(lead.employee_number) !== String(selectedManager));

  return (
    <form onSubmit={handleAssign}>
      {/* Project Dropdown */}
      <div className="form-group" ref={projectRef} style={{ position: 'relative' }}>
        <label className="pmodal-label">Project <span className="required-star">*</span></label>
        <div
          className="pmodal-input"
          style={dropdownTriggerStyle(projectDropdownOpen)}
          onClick={() => {
            setProjectDropdownOpen(o => !o);
            setManagerDropdownOpen(false);
            setLeadDropdownOpen(false);
            setEmployeesDropdownOpen(false);
          }}
        >
          <span style={{ fontSize: '13.5px', color: selectedProjectObj ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
            {selectedProjectObj ? selectedProjectObj.project_name : 'Select Project'}
          </span>
          <FiChevronDown style={chevronStyle(projectDropdownOpen)} />
        </div>
        {projectDropdownOpen && (
          <div style={dropdownListStyle}>
            {projects.length === 0 ? (
              <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>No projects found</div>
            ) : projects.map(project => (
              <div
                key={project._id}
                style={dropdownItemStyle(selectedProject === project._id)}
                onClick={() => {
                  setSelectedProject(project._id);
                  setSelectedProjectObj(project);
                  setProjectDropdownOpen(false);
                  setManagerDropdownOpen(false);
                  setLeadDropdownOpen(false);
                  setEmployeesDropdownOpen(false);

                  // Pre-populate assigned manager if already set
                  if (project.manager_id) {
                    const mgr = subManagers.find(m => String(m.employee_number) === String(project.manager_id));
                    if (mgr) {
                      setSelectedManager(mgr.employee_number);
                      setSelectedManagerObj(mgr);
                    }
                  } else {
                    setSelectedManager('');
                    setSelectedManagerObj(null);
                  }

                  // Pre-populate assigned lead if already set
                  if (project.lead_id) {
                    const ld = leads.find(l => String(l.employee_number) === String(project.lead_id) || String(l._id) === String(project.lead_id));
                    if (ld) {
                      setSelectedLead(ld.employee_number);
                      setSelectedLeadObj(ld);
                    }
                  } else {
                    setSelectedLead('');
                    setSelectedLeadObj(null);
                  }

                  // Do not auto-select employees; start with an empty selection so user can choose who to assign
                  setSelectedEmployees([]);
                }}
                onMouseEnter={e => { if (selectedProject !== project._id) e.currentTarget.style.backgroundColor = 'var(--brand-light, #fef3c7)'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = selectedProject === project._id ? 'var(--brand-light, #fef3c7)' : 'transparent'; }}
              >
                {project.project_name}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Manager Dropdown */}
      <div className="form-group" ref={managerRef} style={{ position: 'relative' }}>
        <label className="pmodal-label">Manager <span className="required-star">*</span></label>
        <div
          className="pmodal-input"
          style={dropdownTriggerStyle(managerDropdownOpen)}
          onClick={() => {
            setManagerDropdownOpen(o => !o);
            setProjectDropdownOpen(false);
            setLeadDropdownOpen(false);
            setEmployeesDropdownOpen(false);
          }}
        >
          <span style={{ fontSize: '13.5px', color: selectedManagerObj ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
            {selectedManagerObj ? selectedManagerObj.full_name : 'Select Manager'}
          </span>
          <FiChevronDown style={chevronStyle(managerDropdownOpen)} />
        </div>
        {managerDropdownOpen && (
          <div style={dropdownListStyle}>
            {subManagers.length === 0 ? (
              <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>No managers found</div>
            ) : subManagers.map(manager => (
              <div
                key={manager._id}
                style={dropdownItemStyle(selectedManager === manager.employee_number)}
                onClick={() => {
                  setSelectedManager(manager.employee_number);
                  setSelectedManagerObj(manager);
                  setManagerDropdownOpen(false);
                  setProjectDropdownOpen(false);
                  setLeadDropdownOpen(false);
                  setEmployeesDropdownOpen(false);
                }}
                onMouseEnter={e => { if (selectedManager !== manager.employee_number) e.currentTarget.style.backgroundColor = 'var(--brand-light, #fef3c7)'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = selectedManager === manager.employee_number ? 'var(--brand-light, #fef3c7)' : 'transparent'; }}
              >
                {manager.full_name}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lead Dropdown */}
      <div className="form-group" ref={leadRef} style={{ position: 'relative' }}>
        <label className="pmodal-label">Lead (Optional)</label>
        <div
          className="pmodal-input"
          style={dropdownTriggerStyle(leadDropdownOpen)}
          onClick={() => {
            setLeadDropdownOpen(o => !o);
            setProjectDropdownOpen(false);
            setManagerDropdownOpen(false);
            setEmployeesDropdownOpen(false);
          }}
        >
          <span style={{ fontSize: '13.5px', color: selectedLeadObj ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
            {selectedLeadObj ? selectedLeadObj.full_name : 'Select Lead'}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {selectedLeadObj && (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedLead('');
                  setSelectedLeadObj(null);
                }}
                style={{ cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1 }}
                title="Clear selection"
              >
                ✕
              </span>
            )}
            <FiChevronDown style={chevronStyle(leadDropdownOpen)} />
          </div>
        </div>
        {leadDropdownOpen && (
          <div style={dropdownListStyle}>
            {filteredLeads.length === 0 ? (
              <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>No leads found</div>
            ) : filteredLeads.map(lead => {
              const isSelected = selectedLead === lead.employee_number;
              return (
                <div
                  key={lead._id || lead.employee_number}
                  style={dropdownItemStyle(isSelected)}
                  onClick={() => {
                    if (isSelected) {
                      setSelectedLead('');
                      setSelectedLeadObj(null);
                    } else {
                      setSelectedLead(lead.employee_number);
                      setSelectedLeadObj(lead);
                    }
                    setLeadDropdownOpen(false);
                    setProjectDropdownOpen(false);
                    setManagerDropdownOpen(false);
                    setEmployeesDropdownOpen(false);
                  }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--brand-light, #fef3c7)'; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = isSelected ? 'var(--brand-light, #fef3c7)' : 'transparent'; }}
                >
                  {lead.full_name}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Employees Multi-Select Dropdown */}
      <div className="form-group" ref={employeesRef} style={{ position: 'relative' }}>
        <label className="pmodal-label">Employees (Optional)</label>
        <div
          className="pmodal-input"
          style={dropdownTriggerStyle(employeesDropdownOpen)}
          onClick={() => {
            setEmployeesDropdownOpen(o => !o);
            setProjectDropdownOpen(false);
            setManagerDropdownOpen(false);
            setLeadDropdownOpen(false);
          }}
        >
          <span style={{ fontSize: '13.5px', color: selectedEmployees.length > 0 ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
            {selectedEmployees.length === 0 ? 'Select Employees' : `Selected Employees (${selectedEmployees.length})`}
          </span>
          <FiChevronDown style={chevronStyle(employeesDropdownOpen)} />
        </div>
        {employeesDropdownOpen && (
          <div style={dropdownListStyle}>
            {employees.length === 0 ? (
              <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>No employees found</div>
            ) : employees.map(emp => {
              const empId = emp.employee_number || emp._id;
              const isChecked = selectedEmployees.some(id => String(id).trim().toLowerCase() === String(empId).trim().toLowerCase());
              const hasProj = emp.current_projects && emp.current_projects.length > 0;
              const projLabel = hasProj ? emp.current_projects.join(', ') : 'No project';
              return (
                <div
                  key={empId}
                  style={{
                    ...dropdownItemStyle(isChecked),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px'
                  }}
                  onClick={() => handleCheckboxChange(empId)}
                  onMouseEnter={e => { if (!isChecked) e.currentTarget.style.backgroundColor = 'var(--brand-light, #fef3c7)'; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = isChecked ? 'var(--brand-light, #fef3c7)' : 'transparent'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                    <input type="checkbox" checked={isChecked} readOnly style={{ cursor: 'pointer', pointerEvents: 'none', flexShrink: 0 }} />
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{emp.full_name}</span>
                  </div>
                  <span
                    style={{
                      fontSize: '11px',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      backgroundColor: hasProj ? '#fef3c7' : '#f1f5f9',
                      color: hasProj ? '#b45309' : '#64748b',
                      border: `1px solid ${hasProj ? '#fde68a' : '#e2e8f0'}`,
                      fontWeight: hasProj ? 600 : 400,
                      whiteSpace: 'nowrap',
                      flexShrink: 0
                    }}
                    title={hasProj ? `Currently working on: ${projLabel}` : 'Not assigned to any project'}
                  >
                    {hasProj ? `Project: ${projLabel}` : 'No project'}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Selected Employees Badge Summary showing current projects */}
        {selectedEmployees.length > 0 && (
          <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>Selected Employees & Current Projects:</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '100px', overflowY: 'auto', padding: '2px 0' }}>
              {selectedEmployees.map(empId => {
                const emp = employees.find(e => String(e.employee_number || e._id).trim().toLowerCase() === String(empId).trim().toLowerCase());
                const empName = emp?.full_name || empId;
                const hasProj = emp?.current_projects && emp.current_projects.length > 0;
                const projDisplay = hasProj ? emp.current_projects.join(', ') : 'No project';
                return (
                  <div
                    key={empId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      backgroundColor: 'var(--bg-elevated, #f8fafc)',
                      border: '1px solid var(--border, #e2e8f0)',
                      borderRadius: '6px',
                      padding: '4px 10px',
                      fontSize: '12px'
                    }}
                  >
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{empName}</span>
                    <span style={{
                      fontSize: '10.5px',
                      padding: '1px 6px',
                      borderRadius: '8px',
                      backgroundColor: hasProj ? '#fef3c7' : '#f1f5f9',
                      color: hasProj ? '#b45309' : '#64748b',
                      fontWeight: 600
                    }}>
                      {projDisplay}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="pmodal-footer">
        <button type="button" className="pmodal-btn-cancel" onClick={onClose}>Cancel</button>
        <button type="submit" className="pmodal-btn pmodal-btn-orange">Assign Project</button>
      </div>
    </form>
  );
};

/* ── Inline: Add Employee to Org ──────────────────────────── */
const AddEmployeeToOrgInline = ({ onClose, onEmployeeAdded }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [emailError, setEmailError] = useState('');
  const showToast = useToast();

  const validateEmail = (value) => {
    if (!value) {
      setEmailError('');
      return;
    }
    // Check it looks like a valid email first, then check domain
    const isValidFormat = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    if (isValidFormat && !value.toLowerCase().endsWith('@tsqsinc.com')) {
      setEmailError('Only @tsqsinc.com email addresses are allowed.');
    } else {
      setEmailError('');
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    validateEmail(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !name || !employeeId) {
      showToast('Please fill in Email, Name, and Employee ID.', 'warning');
      return;
    }

    if (!email.toLowerCase().endsWith('@tsqsinc.com')) {
      setEmailError('Only @tsqsinc.com email addresses are allowed.');
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/api/add-to-org`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name, employeeId }),
      });

      const data = await response.json();

      if (response.ok) {
        setEmail('');
        setName('');
        setEmployeeId('');
        setEmailError('');
        showToast('Employee added to organization successfully!', 'success');
        if (onEmployeeAdded) {
          onEmployeeAdded();
        }
        onClose();
      } else {
        showToast(data.error || 'Failed to add employee to organization.', 'error');
      }
    } catch (error) {
      console.error('Error adding employee to org:', error);
      showToast('An error occurred. Please try again.', 'error');
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label className="pmodal-label">Email <span className="required-star">*</span></label>
        <input
          type="email"
          className={`pmodal-input${emailError ? ' input-error' : ''}`}
          placeholder="Enter employee email (@tsqsinc.com)"
          value={email}
          onChange={handleEmailChange}
          required
        />
        {emailError && (
          <span style={{
            display: 'block',
            marginTop: '4px',
            fontSize: '12px',
            color: '#dc2626',
            fontWeight: '500',
          }}>
            {emailError}
          </span>
        )}
      </div>
      <div className="form-group">
        <label className="pmodal-label">Name <span className="required-star">*</span></label>
        <input
          type="text"
          className="pmodal-input"
          placeholder="Enter employee name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
      </div>
      <div className="form-group">
        <label className="pmodal-label">Employee ID <span className="required-star">*</span></label>
        <input
          type="text"
          className="pmodal-input"
          placeholder="Enter employee ID"
          value={employeeId}
          onChange={(e) => setEmployeeId(e.target.value)}
          required
        />
      </div>
      <div className="pmodal-footer">
        <button type="button" className="pmodal-btn-cancel" onClick={onClose}>Cancel</button>
        <button type="submit" className="pmodal-btn pmodal-btn-orange">Add employee</button>
      </div>
    </form>
  );
};

/* ── Inline: Delete Employee from Org ─────────────────────── */
const DeleteEmployeeFromOrgInline = ({ onClose, onEmployeeDeleted }) => {
  const [employeesList, setEmployeesList] = useState([]);
  const [selectedEmpIds, setSelectedEmpIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = React.useRef(null);
  const showToast = useToast();
  const confirm = useConfirm();

  useEffect(() => {
    fetch(`${apiUrl}/api/all-org-employees`)
      .then((res) => res.json())
      .then((data) => {
        const arr = Array.isArray(data) ? data : [];
        setEmployeesList(arr.filter(emp => emp.role !== 'Main Manager'));
      })
      .catch((err) => {
        console.error('Error fetching employees list:', err);
      })
      .finally(() => {
        setLoading(false);
      });

    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredEmployees = employeesList.filter((emp) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const nameMatch = (emp.name || '').toLowerCase().includes(term);
    const idMatch = String(emp.employee_id || '').toLowerCase().includes(term);
    const emailMatch = (emp.email || '').toLowerCase().includes(term);
    return nameMatch || idMatch || emailMatch;
  });

  const handleToggleEmployee = (employeeId) => {
    const idKey = String(employeeId).trim();
    setSelectedEmpIds((prev) => {
      if (prev.some((id) => String(id).trim().toLowerCase() === idKey.toLowerCase())) {
        return prev.filter((id) => String(id).trim().toLowerCase() !== idKey.toLowerCase());
      } else {
        return [...prev, idKey];
      }
    });
  };

  const handleSelectAll = () => {
    const visibleIds = filteredEmployees.map((emp) => String(emp.employee_id).trim());
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) =>
      selectedEmpIds.some((selected) => String(selected).trim().toLowerCase() === id.toLowerCase())
    );

    if (allVisibleSelected) {
      const visibleSet = new Set(visibleIds.map(id => id.toLowerCase()));
      setSelectedEmpIds((prev) => prev.filter((id) => !visibleSet.has(String(id).trim().toLowerCase())));
    } else {
      const combined = Array.from(new Set([...selectedEmpIds, ...visibleIds]));
      setSelectedEmpIds(combined);
    }
  };

  const handleRemoveSelectedChip = (empIdToRemove) => {
    setSelectedEmpIds((prev) => prev.filter((id) => String(id).trim().toLowerCase() !== String(empIdToRemove).trim().toLowerCase()));
  };

  const handleClearAll = () => {
    setSelectedEmpIds([]);
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    if (selectedEmpIds.length === 0) {
      showToast('Please select at least one employee to delete.', 'warning');
      return;
    }

    const count = selectedEmpIds.length;
    const ok = await confirm({
      title: count === 1 ? 'Delete Employee from Organization' : 'Delete Employees from Organization',
      message: count === 1
        ? 'Are you sure you want to permanently delete this employee from the organization? This will remove them from the organization directory and all assigned managers.'
        : `Are you sure you want to permanently delete these ${count} employees from the organization? This action will remove them from the organization directory and all assigned managers.`,
      confirmText: count === 1 ? 'Delete Employee' : `Delete ${count} Employees`,
      cancelText: 'Cancel',
      confirmVariant: 'danger'
    });

    if (!ok) return;

    setIsDeleting(true);
    try {
      const response = await fetch(`${apiUrl}/api/delete-multiple-from-org`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employeeIds: selectedEmpIds }),
      });

      const data = await response.json();

      if (response.ok) {
        showToast(
          count === 1
            ? 'Employee deleted from organization successfully!'
            : `Successfully deleted ${count} employee(s) from organization!`,
          'success'
        );
        if (onEmployeeDeleted) {
          onEmployeeDeleted(selectedEmpIds);
        }
        setSelectedEmpIds([]);
        onClose();
      } else {
        showToast(data.error || 'Failed to delete employee(s) from organization.', 'error');
      }
    } catch (error) {
      console.error('Error deleting employee(s) from org:', error);
      showToast('An error occurred. Please try again.', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <form onSubmit={handleDelete}>
      <div className="form-group" ref={dropdownRef}>
        <label className="pmodal-label">Select Employees to Delete <span className="required-star">*</span></label>
        
        <div style={{ position: 'relative' }}>
          {/* Dropdown Input Header */}
          <div
            className="pmodal-input"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer',
              userSelect: 'none',
              backgroundColor: 'var(--bg-input)',
              color: selectedEmpIds.length > 0 ? 'var(--text-primary)' : 'var(--text-secondary)',
              borderColor: isDropdownOpen ? 'var(--brand, #f59e0b)' : 'var(--border-strong)',
              boxShadow: isDropdownOpen ? '0 0 0 3px rgba(245, 158, 11, 0.18)' : 'none',
              borderRadius: '8px',
              padding: '10px 14px'
            }}
          >
            <span style={{ fontSize: '13.5px', fontWeight: selectedEmpIds.length > 0 ? '600' : 'normal' }}>
              {selectedEmpIds.length === 0
                ? 'Select Employees to Delete'
                : `${selectedEmpIds.length} employee${selectedEmpIds.length > 1 ? 's' : ''} selected`}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {selectedEmpIds.length > 0 && (
                <span style={{
                  fontSize: '11px',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  backgroundColor: 'var(--brand-light, #fef3c7)',
                  color: 'var(--brand-hover, #ea580c)',
                  fontWeight: '700'
                }}>
                  {selectedEmpIds.length}
                </span>
              )}
              <FiChevronDown 
                style={{ 
                  fontSize: '1.1rem', 
                  color: 'var(--text-secondary)', 
                  transition: 'transform 0.2s ease', 
                  transform: isDropdownOpen ? 'rotate(180deg)' : 'none' 
                }} 
              />
            </div>
          </div>

          {/* Dropdown list popup */}
          {isDropdownOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 1000,
                marginTop: '4px',
                backgroundColor: 'var(--bg-elevated, #ffffff)',
                border: '1px solid var(--border, #e5e7eb)',
                borderRadius: '8px',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.08)',
                maxHeight: '260px',
                overflowY: 'auto',
                boxSizing: 'border-box'
              }}
            >
              {/* Search Bar & Select All Header */}
              <div style={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                backgroundColor: 'var(--bg-elevated, #ffffff)',
                borderBottom: '1px solid var(--border-light, #f1f5f9)',
                padding: '8px 10px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <input
                  type="text"
                  placeholder="Search by name, ID or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    width: '100%',
                    padding: '6px 10px',
                    fontSize: '12.5px',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    backgroundColor: 'var(--bg-input)',
                    color: 'var(--text-primary)',
                    boxSizing: 'border-box'
                  }}
                />
                {filteredEmployees.length > 0 && (
                  <div
                    onClick={handleSelectAll}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '4px 6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      color: 'var(--brand, #ea580c)',
                      fontWeight: '600',
                      userSelect: 'none'
                    }}
                  >
                    <span>
                      {filteredEmployees.every((emp) =>
                        selectedEmpIds.some((id) => String(id).trim().toLowerCase() === String(emp.employee_id).trim().toLowerCase())
                      )
                        ? 'Deselect All'
                        : `Select All (${filteredEmployees.length})`}
                    </span>
                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                      {selectedEmpIds.length} / {employeesList.length} selected
                    </span>
                  </div>
                )}
              </div>

              {/* Employee items */}
              {loading ? (
                <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>
                  Loading...
                </div>
              ) : filteredEmployees.length === 0 ? (
                <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>
                  No employees found
                </div>
              ) : (
                filteredEmployees.map((emp) => {
                  const empId = String(emp.employee_id).trim();
                  const isChecked = selectedEmpIds.some((id) => String(id).trim().toLowerCase() === empId.toLowerCase());
                  return (
                    <div
                      key={emp.employee_id}
                      onClick={() => handleToggleEmployee(empId)}
                      style={{
                        padding: '9px 12px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '10px',
                        backgroundColor: isChecked ? 'var(--brand-light, #fef3c7)' : 'transparent',
                        borderBottom: '1px solid var(--border-light, #f1f5f9)',
                        transition: 'background-color 0.15s ease'
                      }}
                      onMouseEnter={(e) => { if (!isChecked) e.currentTarget.style.backgroundColor = 'var(--brand-light, #fef3c7)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isChecked ? 'var(--brand-light, #fef3c7)' : 'transparent'; }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          readOnly
                          style={{ cursor: 'pointer', pointerEvents: 'none', flexShrink: 0 }}
                        />
                        <div style={{ minWidth: 0 }}>
                          <span style={{ fontWeight: isChecked ? '600' : '500', color: 'var(--text-primary)' }}>
                            {emp.name}
                          </span>
                          <span style={{ fontSize: '11.5px', color: 'var(--text-secondary)', marginLeft: '6px' }}>
                            ({emp.employee_id})
                          </span>
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: '11px',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          backgroundColor: 'rgba(234, 88, 12, 0.12)',
                          color: '#ea580c',
                          fontWeight: '600',
                          whiteSpace: 'nowrap',
                          flexShrink: 0
                        }}
                      >
                        {emp.role || 'Member'}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      {/* Selected Employees Chips Summary */}
      {selectedEmpIds.length > 0 && (
        <div style={{
          marginTop: '14px',
          padding: '12px 14px',
          borderRadius: '8px',
          backgroundColor: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          color: 'var(--text-primary)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <span style={{ fontSize: '12.5px', fontWeight: '600', color: 'var(--text-primary)' }}>
              Selected to Delete ({selectedEmpIds.length})
            </span>
            <button
              type="button"
              onClick={handleClearAll}
              style={{
                background: 'none',
                border: 'none',
                color: '#dc2626',
                fontSize: '11.5px',
                fontWeight: '600',
                cursor: 'pointer',
                padding: '2px 6px'
              }}
            >
              Clear All
            </button>
          </div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px',
            maxHeight: '120px',
            overflowY: 'auto'
          }}>
            {selectedEmpIds.map((id) => {
              const emp = employeesList.find((e) => String(e.employee_id).trim().toLowerCase() === String(id).trim().toLowerCase());
              const name = emp ? emp.name : id;
              return (
                <div
                  key={id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.25)',
                    fontSize: '12px',
                    color: 'var(--text-primary)'
                  }}
                >
                  <span style={{ fontWeight: '600' }}>{name}</span>
                  <span style={{ fontSize: '10.5px', color: 'var(--text-secondary)' }}>({id})</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveSelectedChip(id);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#dc2626',
                      fontWeight: '700',
                      fontSize: '13px',
                      cursor: 'pointer',
                      padding: '0 2px',
                      lineHeight: 1
                    }}
                    title="Remove from selection"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="pmodal-footer">
        <button type="button" className="pmodal-btn-cancel-white" onClick={onClose} disabled={isDeleting}>
          Cancel
        </button>
        <button
          type="submit"
          className="pmodal-btn pmodal-btn-orange"
          disabled={selectedEmpIds.length === 0 || isDeleting}
          style={{
            opacity: selectedEmpIds.length === 0 || isDeleting ? 0.6 : 1,
            cursor: selectedEmpIds.length === 0 || isDeleting ? 'not-allowed' : 'pointer'
          }}
        >
          {isDeleting ? 'Deleting...' : selectedEmpIds.length > 0 ? `Delete (${selectedEmpIds.length})` : 'Delete'}
        </button>
      </div>
    </form>
  );
};

/* ── Inline: Remove Employee from Project ────────────────────── */
const RemoveEmployeeFromProjectInline = ({ onClose, onEmployeeRemoved }) => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedProjectObj, setSelectedProjectObj] = useState(null);
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [assignedEmployees, setAssignedEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [selectedEmployeesToRemove, setSelectedEmployeesToRemove] = useState([]);
  const [isRemoving, setIsRemoving] = useState(false);

  const projectRef = useRef(null);
  const showToast = useToast();
  const confirm = useConfirm();

  // Load projects on mount
  useEffect(() => {
    fetch(`${apiUrl}/api/fetch-projects`)
      .then(r => r.json())
      .then(data => setProjects(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (projectRef.current && !projectRef.current.contains(event.target)) {
        setProjectDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch assigned employees when project changes
  const fetchAssignedEmployees = async (projectId) => {
    if (!projectId) {
      setAssignedEmployees([]);
      return;
    }
    setLoadingEmployees(true);
    setSelectedEmployeesToRemove([]);
    try {
      const res = await fetch(`${apiUrl}/api/project-employees/${projectId}`);
      if (res.ok) {
        const data = await res.json();
        setAssignedEmployees(Array.isArray(data) ? data : []);
      } else {
        setAssignedEmployees([]);
      }
    } catch (err) {
      console.error('Error fetching project employees:', err);
      setAssignedEmployees([]);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const handleSelectProject = (project) => {
    setSelectedProject(project._id);
    setSelectedProjectObj(project);
    setProjectDropdownOpen(false);
    fetchAssignedEmployees(project._id);
  };

  const handleRemoveSingle = async (employee) => {
    const empName = employee.full_name || employee.employee_number;
    const projName = selectedProjectObj?.project_name || 'the project';
    const ok = await confirm({
      title: 'Remove Employee from Project',
      message: `Are you sure you want to remove "${empName}" from "${projName}"?`,
      confirmText: 'Remove',
      cancelText: 'Cancel',
      confirmVariant: 'danger'
    });
    if (!ok) return;

    setIsRemoving(true);
    try {
      const response = await fetch(`${apiUrl}/api/remove-employee-from-project`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: selectedProject,
          employeeNumber: employee.employee_number
        })
      });
      const data = await response.json();
      if (response.ok) {
        showToast(data.message || `Removed ${empName} from "${projName}".`, 'success');
        fetchAssignedEmployees(selectedProject);
        if (onEmployeeRemoved) onEmployeeRemoved();
      } else {
        showToast(data.error || 'Failed to remove employee.', 'error');
      }
    } catch (err) {
      showToast('An error occurred. Please try again.', 'error');
    } finally {
      setIsRemoving(false);
    }
  };

  const handleRemoveSelected = async () => {
    if (selectedEmployeesToRemove.length === 0) return;
    const projName = selectedProjectObj?.project_name || 'the project';
    const ok = await confirm({
      title: 'Remove Selected Employees',
      message: `Are you sure you want to remove ${selectedEmployeesToRemove.length} employee(s) from "${projName}"?`,
      confirmText: 'Remove All',
      cancelText: 'Cancel',
      confirmVariant: 'danger'
    });
    if (!ok) return;

    setIsRemoving(true);
    try {
      const response = await fetch(`${apiUrl}/api/remove-employee-from-project`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: selectedProject,
          employeeNumbers: selectedEmployeesToRemove
        })
      });
      const data = await response.json();
      if (response.ok) {
        showToast(data.message || 'Selected employees removed successfully.', 'success');
        setSelectedEmployeesToRemove([]);
        fetchAssignedEmployees(selectedProject);
        if (onEmployeeRemoved) onEmployeeRemoved();
      } else {
        showToast(data.error || 'Failed to remove employees.', 'error');
      }
    } catch (err) {
      showToast('An error occurred. Please try again.', 'error');
    } finally {
      setIsRemoving(false);
    }
  };

  const handleToggleSelectAll = () => {
    if (selectedEmployeesToRemove.length === assignedEmployees.length) {
      setSelectedEmployeesToRemove([]);
    } else {
      setSelectedEmployeesToRemove(assignedEmployees.map(e => e.employee_number));
    }
  };

  const handleToggleEmployee = (empNumber) => {
    setSelectedEmployeesToRemove(prev =>
      prev.includes(empNumber) ? prev.filter(id => id !== empNumber) : [...prev, empNumber]
    );
  };

  return (
    <div className="pmodal-section">
      <div className="pmodal-header">
        <div className="pmodal-header-icon red-icon">🚫</div>
        <div className="pmodal-header-text">
          <h2>Remove Employee from Project</h2>
          <p>Remove assigned employees from a specific project.</p>
        </div>
      </div>
      <div className="pmodal-divider" />
      <div className="pmodal-body">
        {/* Project Dropdown */}
        <div className="form-group" ref={projectRef} style={{ position: 'relative' }}>
          <label className="pmodal-label">Project <span className="required-star">*</span></label>
          <div
            className="pmodal-input"
            onClick={() => setProjectDropdownOpen(o => !o)}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer',
              userSelect: 'none',
              backgroundColor: 'var(--bg-input)',
              color: selectedProjectObj ? 'var(--text-primary)' : 'var(--text-secondary)',
              borderColor: projectDropdownOpen ? 'var(--brand, #f59e0b)' : 'var(--border-strong)',
              boxShadow: projectDropdownOpen ? '0 0 0 3px rgba(245, 158, 11, 0.18)' : 'none',
              borderRadius: '8px',
              padding: '10px 14px'
            }}
          >
            <span style={{ fontSize: '13.5px' }}>
              {selectedProjectObj ? selectedProjectObj.project_name : 'Select Project'}
            </span>
            <FiChevronDown
              style={{
                fontSize: '1.1rem',
                color: 'var(--text-secondary)',
                transition: 'transform 0.2s ease',
                transform: projectDropdownOpen ? 'rotate(180deg)' : 'none'
              }}
            />
          </div>
          {projectDropdownOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 1000,
                marginTop: '4px',
                backgroundColor: 'var(--bg-elevated, #ffffff)',
                border: '1px solid var(--border, #e5e7eb)',
                borderRadius: '8px',
                boxShadow: '0 10px 25px -5px rgba(0,0,0,0.12)',
                maxHeight: '200px',
                overflowY: 'auto',
                boxSizing: 'border-box'
              }}
            >
              {projects.length === 0 ? (
                <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>
                  No projects found
                </div>
              ) : projects.map(project => (
                <div
                  key={project._id}
                  onClick={() => handleSelectProject(project)}
                  style={{
                    padding: '10px 16px',
                    cursor: 'pointer',
                    fontSize: '13.5px',
                    color: selectedProject === project._id ? 'var(--brand, #f59e0b)' : 'var(--text-primary)',
                    fontWeight: selectedProject === project._id ? '600' : '500',
                    backgroundColor: selectedProject === project._id ? 'var(--brand-light, #fef3c7)' : 'transparent',
                    borderBottom: '1px solid var(--border-light, #f1f5f9)',
                    transition: 'background-color 0.15s ease'
                  }}
                  onMouseEnter={e => {
                    if (selectedProject !== project._id) e.currentTarget.style.backgroundColor = 'var(--brand-light, #fef3c7)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = selectedProject === project._id ? 'var(--brand-light, #fef3c7)' : 'transparent';
                  }}
                >
                  {project.project_name}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Assigned Employees List */}
        <div className="form-group" style={{ marginTop: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <label className="pmodal-label" style={{ margin: 0 }}>
              Assigned Employees {assignedEmployees.length > 0 && `(${assignedEmployees.length})`}
            </label>
            {assignedEmployees.length > 1 && (
              <button
                type="button"
                onClick={handleToggleSelectAll}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--brand, #ea580c)',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  padding: '0 4px'
                }}
              >
                {selectedEmployeesToRemove.length === assignedEmployees.length ? 'Deselect All' : 'Select All'}
              </button>
            )}
          </div>

          <div
            className="assigned-employee-box"
            style={{
              maxHeight: '260px',
              minHeight: '100px',
              padding: assignedEmployees.length > 0 ? '4px 8px' : '16px'
            }}
          >
            {!selectedProject ? (
              <p className="pmodal-helper" style={{ textAlign: 'center', margin: '20px 0' }}>
                Select a project above to view assigned employees.
              </p>
            ) : loadingEmployees ? (
              <p className="pmodal-helper" style={{ textAlign: 'center', margin: '20px 0' }}>
                Loading assigned employees...
              </p>
            ) : assignedEmployees.length === 0 ? (
              <p className="pmodal-helper" style={{ textAlign: 'center', margin: '20px 0' }}>
                No employees are currently assigned to this project.
              </p>
            ) : (
              <ul className="assigned-employee-list">
                {assignedEmployees.map((employee) => {
                  const isChecked = selectedEmployeesToRemove.includes(employee.employee_number);
                  return (
                    <li
                      key={employee.employee_number}
                      className="assigned-employee-item"
                      style={{
                        padding: '10px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '12px',
                        borderRadius: '6px',
                        transition: 'background-color 0.15s ease'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleToggleEmployee(employee.employee_number)}
                          style={{ cursor: 'pointer', flexShrink: 0 }}
                          aria-label={`Select ${employee.full_name}`}
                        />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span className="emp-name" style={{ fontSize: '13.5px' }}>
                              {employee.full_name}
                            </span>
                            {employee.role && (
                              <span
                                style={{
                                  fontSize: '11px',
                                  padding: '1px 7px',
                                  borderRadius: '10px',
                                  backgroundColor: employee.role === 'lead' ? '#e0e7ff' : '#f1f5f9',
                                  color: employee.role === 'lead' ? '#3730a3' : '#475569',
                                  fontWeight: '600',
                                  textTransform: 'capitalize'
                                }}
                              >
                                {employee.role}
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '10px', fontSize: '12px', color: 'var(--text-muted)' }}>
                            <span className="emp-id">{employee.employee_number}</span>
                            {employee.job_title && <span>• {employee.job_title}</span>}
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleRemoveSingle(employee)}
                        disabled={isRemoving}
                        title="Remove from project"
                        style={{
                          padding: '5px 12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          color: '#dc2626',
                          backgroundColor: 'transparent',
                          border: '1px solid #fca5a5',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          transition: 'all 0.15s ease',
                          flexShrink: 0
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.backgroundColor = '#fee2e2';
                          e.currentTarget.style.borderColor = '#dc2626';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.borderColor = '#fca5a5';
                        }}
                      >
                        Remove
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="pmodal-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          {selectedEmployeesToRemove.length > 0 && (
            <button
              type="button"
              onClick={handleRemoveSelected}
              disabled={isRemoving}
              style={{
                padding: '8px 16px',
                fontSize: '13px',
                fontWeight: '600',
                color: '#ffffff',
                backgroundColor: '#dc2626',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'background-color 0.15s ease'
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#b91c1c'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#dc2626'}
            >
              Remove Selected ({selectedEmployeesToRemove.length})
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button type="button" className="pmodal-btn-cancel" onClick={onClose} disabled={isRemoving}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const MESection = ({ managerName = "Sudheer", onEmployeeDeleted, onEmployeeAdded, onProjectCreated }) => {
  const showToast = useToast();
  const confirm = useConfirm();
  const [subManagers, setSubManagers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [selectedSubManagerForAssignment, setSelectedSubManagerForAssignment] =
    useState(null); // For assignment in section 1
  const [selectedSubManagerForDetails, setSelectedSubManagerForDetails] =
    useState(null); // For viewing details in section 2
  const [selectedEmployees, setSelectedEmployees] = useState([]); // Track selected employees
  const [isEmployeeDropdownVisible, setEmployeeDropdownVisible] =
    useState(false); // Toggle dropdown visibility
  const [isManagerDropdownVisible, setManagerDropdownVisible] = useState(false);
  const [isManagerDetailsDropdownVisible, setManagerDetailsDropdownVisible] = useState(false);
  const [isConvertEmployeeDropdownVisible, setConvertEmployeeDropdownVisible] = useState(false);
  const [isConvertLeadEmployeeDropdownVisible, setConvertLeadEmployeeDropdownVisible] = useState(false);
  const assignManagerRef = useRef(null);
  const assignEmployeeRef = useRef(null);
  const detailsManagerRef = useRef(null);
  const convertEmployeeRef = useRef(null);
  const convertLeadEmployeeRef = useRef(null);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [dialogMessage, setDialogMessage] = useState("");
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const [activeModal, setActiveModal] = useState(null); // Track which modal is open

  const fetchOrgData = () => {
    // Fetch sub-managers and employees
    axios
      .get(`${apiUrl}/api/sub-managers`)
      .then((response) => {
        setSubManagers(response.data);
      })
      .catch((error) => {
        console.error("Error fetching sub-managers:", error);
      });

    axios
      .get(`${apiUrl}/api/sub-manager-employees`)
      .then((response) => {
        console.log("Employees fetched:", response.data); // Log the fetched employees
        setEmployees(response.data);
      })
      .catch((error) => {
        console.error("Error fetching employees:", error);
      });
  };

  const handleOrgEmployeeDeleted = (deletedEmpId) => {
    fetchOrgData();
    if (onEmployeeDeleted) {
      onEmployeeDeleted(deletedEmpId);
    }
  };

  const handleOrgEmployeeAdded = () => {
    fetchOrgData();
    if (onEmployeeAdded) {
      onEmployeeAdded();
    }
  };

  const handleOrgProjectCreated = () => {
    fetchOrgData();
    if (onProjectCreated) {
      onProjectCreated();
    }
  };

  useEffect(() => {
    fetchOrgData();
  }, []);

  // Handle outside clicks to close all dropdowns in MESection modals
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (assignManagerRef.current && !assignManagerRef.current.contains(e.target)) {
        setManagerDropdownVisible(false);
      }
      if (assignEmployeeRef.current && !assignEmployeeRef.current.contains(e.target)) {
        setEmployeeDropdownVisible(false);
      }
      if (detailsManagerRef.current && !detailsManagerRef.current.contains(e.target)) {
        setManagerDetailsDropdownVisible(false);
      }
      if (convertEmployeeRef.current && !convertEmployeeRef.current.contains(e.target)) {
        setConvertEmployeeDropdownVisible(false);
      }
      if (convertLeadEmployeeRef.current && !convertLeadEmployeeRef.current.contains(e.target)) {
        setConvertLeadEmployeeDropdownVisible(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    if (selectedSubManagerForDetails) {
      // Fetch assignments for the selected sub-manager in Section 2 (details)
      axios
        .get(`${apiUrl}/api/assigned-employees/${selectedSubManagerForDetails}`)
        .then((response) => {
          setAssignments(response.data);
          console.log("employeeunderamanager", response.data);
        })
        .catch((error) => {
          console.error("Error fetching assignments for sub-manager:", error);
        });
    }
  }, [selectedSubManagerForDetails]);

  const handleAssignEmployee = async () => {
    if (!selectedSubManagerForAssignment) {
      showToast("Please select a sub-manager.", "warning");
      return;
    }
    if (selectedEmployees.length === 0) {
      showToast("Please select at least one employee.", "warning");
      return;
    }

    try {
      const response = await axios.post(`${apiUrl}/api/assign-employees`, {
        subManagerId: selectedSubManagerForAssignment,
        employeeIds: selectedEmployees,
      });
      showToast(response.data.message || "Employees assigned successfully!", "success");
      setSelectedEmployees([]); // Clear selected employees after assignment
    } catch (error) {
      console.error("Error assigning employees:", error);
      if (error.response && error.response.status === 409) {
          showToast(error.response.data.message || "Employees already assigned.", "warning");
      } else {
          showToast("Failed to assign employees. Please try again.", "error");
      }
    }
  };

  // Handle the checkbox change for employees
  const handleEmployeeCheckboxChange = (employeeNumber) => {
    setManagerDropdownVisible(false);
    setSelectedEmployees((prevSelected) => {
      if (prevSelected.includes(employeeNumber)) {
        return prevSelected.filter((emp) => emp !== employeeNumber); // Remove from selection
      } else {
        return [...prevSelected, employeeNumber]; // Add to selection
      }
    });
  };

  //conversion of employee to manager
  const handleConvertEmployee = async () => {
    if (!selectedEmployee) {
      showToast("Please select an employee to convert.", "warning");
      return;
    }

    const ok = await confirm({
      title: 'Promote to Manager',
      message: 'Are you sure you want to promote this employee to a Manager role? This action will change their system access.',
      confirmLabel: 'Promote',
      cancelLabel: 'Cancel',
      variant: 'info'
    });
    if (!ok) return;

    // Make the API call to convert the employee
    axios
      .post(`${apiUrl}/api/convert-to-sub-manager`, {
        employeeId: selectedEmployee,
      })
      .then((response) => {
        // Retrieve the employee name from the response
        const employeeName = response.data.name || selectedEmployee; // Fallback to ID if name is not available

        // Show dialog message
        setDialogMessage(
          `Employee ${employeeName} successfully converted to Sub-Manager (Admin).`,
        );
        setIsDialogVisible(true);

        // Remove the converted employee from the lists
        setEmployees(
          employees.filter(
            (emp) => emp.employee_number !== selectedEmployee,
          ),
        );
        setFilteredEmployees(
          filteredEmployees.filter(
            (emp) => emp.employee_number !== selectedEmployee,
          ),
        );
        setSelectedEmployee(null); // Reset selection
        setSearchTerm(""); // Reset search term

        // Refetch the sub-managers list so the new manager appears in the dropdown immediately
        axios.get(`${apiUrl}/api/sub-managers`)
          .then((res) => setSubManagers(res.data))
          .catch((err) => console.error("Error refreshing sub-managers:", err));

        if (onEmployeeAdded) onEmployeeAdded();

        // Hide dialog after 15 seconds
        setTimeout(() => {
          setIsDialogVisible(false);
          setDialogMessage(""); // Clear the message after hiding
        }, 15000);
      })
      .catch((error) => {
        console.error("Error converting employee:", error);
        showToast("Failed to convert employee. Please try again.", "error");
      });
  };

  //conversion of employee to lead
  const handleConvertEmployeeToLead = async () => {
    if (!selectedEmployee) {
      showToast("Please select an employee to convert.", "warning");
      return;
    }

    const ok = await confirm({
      title: 'Promote to Lead',
      message: 'Are you sure you want to promote this employee to a Lead role? This action will change their system access.',
      confirmLabel: 'Promote',
      cancelLabel: 'Cancel',
      variant: 'info'
    });
    if (!ok) return;

    // Make the API call to convert the employee
    axios
      .post(`${apiUrl}/api/convert-to-lead`, {
        employeeId: selectedEmployee,
      })
      .then((response) => {
        // Retrieve the employee name from the response
        const employeeName = response.data.name || selectedEmployee; // Fallback to ID if name is not available

        // Show dialog message
        setDialogMessage(
          `Employee ${employeeName} successfully converted to Lead.`,
        );
        setIsDialogVisible(true);

        // Remove the converted employee from the list
        setEmployees(
          employees.filter(
            (emp) => emp.employee_number !== selectedEmployee,
          ),
        );
        setFilteredEmployees(
          filteredEmployees.filter(
            (emp) => emp.employee_number !== selectedEmployee,
          ),
        );
        setSelectedEmployee(null); // Reset selection
        setSearchTerm(""); // Reset search term

        // Refetch the leads list if needed, or just let it update on next fetch
        if (onEmployeeAdded) onEmployeeAdded();

        // Hide dialog after 15 seconds
        setTimeout(() => {
          setIsDialogVisible(false);
          setDialogMessage(""); // Clear the message after hiding
        }, 15000);
      })
      .catch((error) => {
        console.error("Error converting employee to lead:", error);
        showToast("Failed to convert employee. Please try again.", "error");
      });
  };

  // Handle employee search
  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);

    setFilteredEmployees(
      employees.filter((employee) => {
        // Check if employee.full_name is not null or undefined
        if (employee.full_name) {
          return employee.full_name.toLowerCase().includes(value.toLowerCase());
        }
        return false; // Exclude employees without a full_name
      }),
    );
  };

  const closeModal = () => {
    setActiveModal(null);
    // Clear states when closing modal to refresh views
    setSelectedSubManagerForAssignment(null);
    setSelectedSubManagerForDetails(null);
    setSelectedEmployees([]);
    setEmployeeDropdownVisible(false);
    setManagerDetailsDropdownVisible(false);
    setConvertEmployeeDropdownVisible(false);
    setConvertLeadEmployeeDropdownVisible(false);
    setSearchTerm("");
    setSelectedEmployee(null);
  };

  return (
    <div className="me-section-content">
      <div className="me-greeting-header">
        <h1 className="me-greeting-title">Hi {managerName}...</h1>
        <p className="me-greeting-subtitle">What would you like to do today?</p>
      </div>

      <div className="flex-container">
        
        {/* Dashboard Card 1 */}
        <div className="dashboard-card" onClick={() => setActiveModal('addOrg')}>
          <div className="card-top-row">
            <div className="card-icon-box blue-bg">🏢</div>
            <h3 className="card-title-text">Add Employee to Organization</h3>
          </div>
          <p className="card-desc-text">Add a new employee to the organization database.</p>
          <div className="card-footer-row">
            <div className="card-arrow-box blue-border"><FiArrowRight size={16} /></div>
          </div>
        </div>

        {/* Dashboard Card 2 */}
        <div className="dashboard-card" onClick={() => setActiveModal('assign')}>
          <div className="card-top-row">
            <div className="card-icon-box purple-bg">👥</div>
            <h3 className="card-title-text">Assign Employees to Manager</h3>
          </div>
          <p className="card-desc-text">Assign employees to a specific manager.</p>
          <div className="card-footer-row">
            <div className="card-arrow-box purple-border"><FiArrowRight size={16} /></div>
          </div>
        </div>

        {/* Dashboard Card 3 */}
        <div className="dashboard-card" onClick={() => setActiveModal('details')}>
          <div className="card-top-row">
            <div className="card-icon-box blue-bg">🛡️</div>
            <h3 className="card-title-text">Assigned Employee to Manager</h3>
          </div>
          <p className="card-desc-text">View employees already assigned to a manager.</p>
          <div className="card-footer-row">
            <div className="card-arrow-box blue-border"><FiArrowRight size={16} /></div>
          </div>
        </div>

        {/* Dashboard Card 4 */}
        <div className="dashboard-card" onClick={() => setActiveModal('convert')}>
          <div className="card-top-row">
            <div className="card-icon-box green-bg">⇄</div>
            <h3 className="card-title-text">Convert Employee to Manager</h3>
          </div>
          <p className="card-desc-text">Convert an employee into a sub-manager.</p>
          <div className="card-footer-row">
            <div className="card-arrow-box green-border"><FiArrowRight size={16} /></div>
          </div>
        </div>

        {/* Dashboard Card 5 */}
        <div className="dashboard-card" onClick={() => setActiveModal('convertLead')}>
          <div className="card-top-row">
            <div className="card-icon-box blue-bg">⇄</div>
            <h3 className="card-title-text">Convert Employee to Lead</h3>
          </div>
          <p className="card-desc-text">Convert an employee into a lead.</p>
          <div className="card-footer-row">
            <div className="card-arrow-box blue-border"><FiArrowRight size={16} /></div>
          </div>
        </div>

        {/* Dashboard Card 6 */}
        <div className="dashboard-card" onClick={() => setActiveModal('upload')}>
          <div className="card-top-row">
            <div className="card-icon-box purple-bg">📁</div>
            <h3 className="card-title-text">Project Management</h3>
          </div>
          <p className="card-desc-text">View, edit, and create organization projects.</p>
          <div className="card-footer-row">
            <div className="card-arrow-box purple-border"><FiArrowRight size={16} /></div>
          </div>
        </div>

        {/* Dashboard Card 7 */}
        <div className="dashboard-card" onClick={() => setActiveModal('assignProject')}>
          <div className="card-top-row">
            <div className="card-icon-box orange-bg">📝</div>
            <h3 className="card-title-text">Assign Project to Manager</h3>
          </div>
          <p className="card-desc-text">Assign a project to a specific manager.</p>
          <div className="card-footer-row">
            <div className="card-arrow-box orange-border"><FiArrowRight size={16} /></div>
          </div>
        </div>

        {/* Dashboard Card 8 */}
        <div className="dashboard-card" onClick={() => setActiveModal('deleteOrg')}>
          <div className="card-top-row">
            <div className="card-icon-box red-bg">🗑️</div>
            <h3 className="card-title-text">Delete Employee from Organization</h3>
          </div>
          <p className="card-desc-text">Delete an employee from the organization database.</p>
          <div className="card-footer-row">
            <div className="card-arrow-box red-border"><FiArrowRight size={16} /></div>
          </div>
        </div>

        {/* Dashboard Card 9 */}
        <div className="dashboard-card" onClick={() => setActiveModal('removeEmployeeProject')}>
          <div className="card-top-row">
            <div className="card-icon-box red-bg">🚫</div>
            <h3 className="card-title-text">Remove Employee from Project</h3>
          </div>
          <p className="card-desc-text">Remove an assigned employee from a project.</p>
          <div className="card-footer-row">
            <div className="card-arrow-box red-border"><FiArrowRight size={16} /></div>
          </div>
        </div>

      </div>

      {/* MODALS */}
      {activeModal && (
        <div className="me-modal-overlay" onClick={closeModal}>
          <div
            className={`me-modal-content ${(activeModal === 'deleteOrg' || activeModal === 'assign' || activeModal === 'assignProject' || activeModal === 'removeEmployeeProject') ? 'overflow-visible' : ''} ${activeModal === 'upload' ? 'project-mgmt-modal-content' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="me-modal-close" onClick={closeModal}>✕</button>
            
            {activeModal === 'assign' && (
              <div className="employee-assignment-section">
                <div className="assign-modal-header">
                  <div className="assign-modal-header-icon">👥</div>
                  <div className="assign-modal-header-text">
                    <h2>Assign Employees to Manager</h2>
                    <p>Assign employees to a specific manager.</p>
                  </div>
                </div>
                <div className="assign-modal-divider" />
                <div className="assign-modal-body">
                  <div className="form-group" ref={assignManagerRef} style={{ position: 'relative' }}>
                    <label className="assign-modal-label">Manager <span className="required-star">*</span></label>
                    <div
                      className="pmodal-input"
                      onClick={() => {
                        setManagerDropdownVisible(prev => !prev);
                        setEmployeeDropdownVisible(false);
                      }}
                      style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        cursor: 'pointer', userSelect: 'none',
                        backgroundColor: 'var(--bg-input)',
                        color: selectedSubManagerForAssignment ? 'var(--text-primary)' : 'var(--text-secondary)',
                        borderColor: isManagerDropdownVisible ? 'var(--brand, #f59e0b)' : 'var(--border-strong)',
                        boxShadow: isManagerDropdownVisible ? '0 0 0 3px rgba(245, 158, 11, 0.18)' : 'none',
                        borderRadius: '8px', padding: '10px 14px'
                      }}
                    >
                      <span style={{ fontSize: '13.5px' }}>
                        {selectedSubManagerForAssignment
                          ? (subManagers.find(m => m.employee_number === selectedSubManagerForAssignment)?.full_name || 'Select Manager')
                          : 'Select Manager'}
                      </span>
                      <FiChevronDown style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', transition: 'transform 0.2s ease', transform: isManagerDropdownVisible ? 'rotate(180deg)' : 'none' }} />
                    </div>
                    {isManagerDropdownVisible && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, marginTop: '4px', backgroundColor: 'var(--bg-elevated, #ffffff)', border: '1px solid var(--border, #e5e7eb)', borderRadius: '8px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.12)', maxHeight: '200px', overflowY: 'auto', boxSizing: 'border-box' }}>
                        {subManagers.length === 0 ? (
                          <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>No managers found</div>
                        ) : subManagers.map(subManager => (
                          <div
                            key={subManager.employee_number}
                            onClick={() => {
                              setSelectedSubManagerForAssignment(subManager.employee_number);
                              setManagerDropdownVisible(false);
                              setEmployeeDropdownVisible(false);
                            }}
                            style={{ padding: '10px 16px', cursor: 'pointer', fontSize: '13.5px', color: selectedSubManagerForAssignment === subManager.employee_number ? 'var(--brand, #f59e0b)' : 'var(--text-primary)', fontWeight: selectedSubManagerForAssignment === subManager.employee_number ? '600' : '500', backgroundColor: selectedSubManagerForAssignment === subManager.employee_number ? 'var(--brand-light, #fef3c7)' : 'transparent', borderBottom: '1px solid var(--border-light, #f1f5f9)', transition: 'background-color 0.15s ease' }}
                            onMouseEnter={e => { if (selectedSubManagerForAssignment !== subManager.employee_number) e.currentTarget.style.backgroundColor = 'var(--brand-light, #fef3c7)'; }}
                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = selectedSubManagerForAssignment === subManager.employee_number ? 'var(--brand-light, #fef3c7)' : 'transparent'; }}
                          >
                            {subManager.full_name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="form-group" ref={assignEmployeeRef}>
                    <label className="assign-modal-label">Employees <span className="required-star">*</span></label>
                    <div style={{ position: 'relative' }}>
                      <div
                        className="pmodal-input"
                        onClick={() => {
                          setEmployeeDropdownVisible(prev => !prev);
                          setManagerDropdownVisible(false);
                        }}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          userSelect: 'none',
                          backgroundColor: 'var(--bg-input)',
                          color: selectedEmployees.length > 0 ? 'var(--text-primary)' : 'var(--text-secondary)',
                          borderColor: isEmployeeDropdownVisible ? 'var(--brand, #f59e0b)' : 'var(--border-strong)',
                          boxShadow: isEmployeeDropdownVisible ? '0 0 0 3px rgba(245, 158, 11, 0.18)' : 'none',
                          borderRadius: '8px',
                          padding: '10px 14px'
                        }}
                      >
                        <span style={{ fontSize: '13.5px' }}>
                          {selectedEmployees.length === 0 ? "Select Employees" : `Selected Employees (${selectedEmployees.length})`}
                        </span>
                        <FiChevronDown 
                          style={{ 
                            fontSize: '1.1rem', 
                            color: 'var(--text-secondary)', 
                            transition: 'transform 0.2s ease', 
                            transform: isEmployeeDropdownVisible ? 'rotate(180deg)' : 'none' 
                          }} 
                        />
                      </div>
                      {isEmployeeDropdownVisible && (
                        <div className="me-dropdown-container" style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          zIndex: 1000,
                          marginTop: '4px',
                          backgroundColor: 'var(--bg-elevated, #ffffff)',
                          border: '1px solid var(--border, #e5e7eb)',
                          borderRadius: '8px',
                          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.08)',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          boxSizing: 'border-box'
                        }}>
                          {employees.map((employee) => (
                            <div
                              key={employee.employee_number}
                              className="checkbox-item"
                              onClick={() => setManagerDropdownVisible(false)}
                            >
                              <input
                                type="checkbox"
                                id={`employee-${employee.employee_number}`}
                                checked={selectedEmployees.includes(employee.employee_number)}
                                onChange={() => handleEmployeeCheckboxChange(employee.employee_number)}
                              />
                              <label htmlFor={`employee-${employee.employee_number}`}>{employee.full_name}</label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="assign-modal-helper">You can select multiple employees.</p>
                  </div>
                </div>
                <div className="assign-modal-footer">
                  <button className="assign-btn-cancel" onClick={closeModal}>Cancel</button>
                  <button className="assign-btn-primary" onClick={() => { handleAssignEmployee(); closeModal(); }}>Assign Employees</button>
                </div>
              </div>
            )}

            {activeModal === 'details' && (
              <div className="pmodal-section">
                <div className="pmodal-header">
                  <div className="pmodal-header-icon blue-icon">🛡️</div>
                  <div className="pmodal-header-text">
                    <h2>Assigned Employees to Manager</h2>
                    <p>View employees already assigned to a manager.</p>
                  </div>
                </div>
                <div className="pmodal-divider" />
                <div className="pmodal-body">
                  <div className="form-group" ref={detailsManagerRef} style={{ position: 'relative' }}>
                    <label className="pmodal-label">Manager <span className="required-star">*</span></label>
                    <div
                      className="pmodal-input"
                      onClick={() => setManagerDetailsDropdownVisible(!isManagerDetailsDropdownVisible)}
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none', backgroundColor: 'var(--bg-input)', color: selectedSubManagerForDetails ? 'var(--text-primary)' : 'var(--text-secondary)', borderColor: isManagerDetailsDropdownVisible ? 'var(--brand, #f59e0b)' : 'var(--border-strong)', boxShadow: isManagerDetailsDropdownVisible ? '0 0 0 3px rgba(245, 158, 11, 0.18)' : 'none', borderRadius: '8px', padding: '10px 14px' }}
                    >
                      <span style={{ fontSize: '13.5px' }}>
                        {selectedSubManagerForDetails
                          ? (subManagers.find(m => m.employee_number === selectedSubManagerForDetails)?.full_name || 'Select Manager')
                          : 'Select Manager'}
                      </span>
                      <FiChevronDown style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', transition: 'transform 0.2s ease', transform: isManagerDetailsDropdownVisible ? 'rotate(180deg)' : 'none' }} />
                    </div>
                    {isManagerDetailsDropdownVisible && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, marginTop: '4px', backgroundColor: 'var(--bg-elevated, #ffffff)', border: '1px solid var(--border, #e5e7eb)', borderRadius: '8px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.12)', maxHeight: '200px', overflowY: 'auto', boxSizing: 'border-box' }}>
                        {subManagers.length === 0 ? (
                          <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>No managers found</div>
                        ) : subManagers.map(subManager => (
                          <div
                            key={subManager.employee_number}
                            onClick={() => { setSelectedSubManagerForDetails(subManager.employee_number); setManagerDetailsDropdownVisible(false); }}
                            style={{ padding: '10px 16px', cursor: 'pointer', fontSize: '13.5px', color: selectedSubManagerForDetails === subManager.employee_number ? 'var(--brand, #f59e0b)' : 'var(--text-primary)', fontWeight: selectedSubManagerForDetails === subManager.employee_number ? '600' : '500', backgroundColor: selectedSubManagerForDetails === subManager.employee_number ? 'var(--brand-light, #fef3c7)' : 'transparent', borderBottom: '1px solid var(--border-light, #f1f5f9)', transition: 'background-color 0.15s ease' }}
                            onMouseEnter={e => { if (selectedSubManagerForDetails !== subManager.employee_number) e.currentTarget.style.backgroundColor = 'var(--brand-light, #fef3c7)'; }}
                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = selectedSubManagerForDetails === subManager.employee_number ? 'var(--brand-light, #fef3c7)' : 'transparent'; }}
                          >
                            {subManager.full_name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="pmodal-label">Assigned Employees</label>
                    <div className="assigned-employee-box">
                      {!selectedSubManagerForDetails ? (
                        <p className="pmodal-helper">Select a manager to view assigned employees.</p>
                      ) : assignments.length === 0 ? (
                        <p className="pmodal-helper">No employees assigned to this manager yet.</p>
                      ) : (
                        <ul className="assigned-employee-list">
                          {assignments.map((assignment) => (
                            <li key={assignment.employee_number} className="assigned-employee-item">
                              <span className="emp-name">{assignment.full_name}</span>
                              <span className="emp-id">{assignment.employee_number}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
                <div className="pmodal-footer">
                  <button className="pmodal-btn-cancel" onClick={closeModal}>Cancel</button>
                  <button className="pmodal-btn pmodal-btn-orange" onClick={closeModal}>Close</button>
                </div>
              </div>
            )}

            {activeModal === 'convert' && (
              <div className="pmodal-section">
                <div className="pmodal-header">
                  <div className="pmodal-header-icon green-icon">🔄</div>
                  <div className="pmodal-header-text">
                    <h2>Convert Employee to Manager</h2>
                    <p>Convert an employee into a manager.</p>
                  </div>
                </div>
                <div className="pmodal-divider" />
                <div className="pmodal-body">
                  <div className="form-group" ref={convertEmployeeRef} style={{ position: 'relative' }}>
                    <label className="pmodal-label">Employee <span className="required-star">*</span></label>
                    <div
                      className="pmodal-input"
                      onClick={() => setConvertEmployeeDropdownVisible(!isConvertEmployeeDropdownVisible)}
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none', backgroundColor: 'var(--bg-input)', color: selectedEmployee ? 'var(--text-primary)' : 'var(--text-secondary)', borderColor: isConvertEmployeeDropdownVisible ? 'var(--brand, #f59e0b)' : 'var(--border-strong)', boxShadow: isConvertEmployeeDropdownVisible ? '0 0 0 3px rgba(245, 158, 11, 0.18)' : 'none', borderRadius: '8px', padding: '10px 14px' }}
                    >
                      <span style={{ fontSize: '13.5px' }}>
                        {selectedEmployee
                          ? (employees.find(e => e.employee_number === selectedEmployee)?.full_name || 'Select Employee')
                          : 'Select Employee'}
                      </span>
                      <FiChevronDown style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', transition: 'transform 0.2s ease', transform: isConvertEmployeeDropdownVisible ? 'rotate(180deg)' : 'none' }} />
                    </div>
                    {isConvertEmployeeDropdownVisible && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, marginTop: '4px', backgroundColor: 'var(--bg-elevated, #ffffff)', border: '1px solid var(--border, #e5e7eb)', borderRadius: '8px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.12)', maxHeight: '200px', overflowY: 'auto', boxSizing: 'border-box' }}>
                        {employees.length === 0 ? (
                          <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>No employees found</div>
                        ) : employees.map(emp => (
                          <div
                            key={emp.employee_number}
                            onClick={() => { setSelectedEmployee(emp.employee_number); setConvertEmployeeDropdownVisible(false); }}
                            style={{ padding: '10px 16px', cursor: 'pointer', fontSize: '13.5px', color: selectedEmployee === emp.employee_number ? 'var(--brand, #f59e0b)' : 'var(--text-primary)', fontWeight: selectedEmployee === emp.employee_number ? '600' : '500', backgroundColor: selectedEmployee === emp.employee_number ? 'var(--brand-light, #fef3c7)' : 'transparent', borderBottom: '1px solid var(--border-light, #f1f5f9)', transition: 'background-color 0.15s ease' }}
                            onMouseEnter={e => { if (selectedEmployee !== emp.employee_number) e.currentTarget.style.backgroundColor = 'var(--brand-light, #fef3c7)'; }}
                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = selectedEmployee === emp.employee_number ? 'var(--brand-light, #fef3c7)' : 'transparent'; }}
                          >
                            {emp.full_name || 'Name not available'}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="pmodal-footer">
                  <button className="pmodal-btn-cancel" onClick={closeModal}>Cancel</button>
                  <button className="pmodal-btn pmodal-btn-orange" onClick={() => { handleConvertEmployee(); closeModal(); }}>Convert to Manager</button>
                </div>
              </div>
            )}

            {activeModal === 'convertLead' && (
              <div className="pmodal-section">
                <div className="pmodal-header">
                  <div className="pmodal-header-icon blue-icon">🔄</div>
                  <div className="pmodal-header-text">
                    <h2>Convert Employee to Lead</h2>
                    <p>Convert an employee into a lead.</p>
                  </div>
                </div>
                <div className="pmodal-divider" />
                <div className="pmodal-body">
                  <div className="form-group" ref={convertLeadEmployeeRef} style={{ position: 'relative' }}>
                    <label className="pmodal-label">Employee <span className="required-star">*</span></label>
                    <div
                      className="pmodal-input"
                      onClick={() => setConvertLeadEmployeeDropdownVisible(!isConvertLeadEmployeeDropdownVisible)}
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none', backgroundColor: 'var(--bg-input)', color: selectedEmployee ? 'var(--text-primary)' : 'var(--text-secondary)', borderColor: isConvertLeadEmployeeDropdownVisible ? 'var(--brand, #f59e0b)' : 'var(--border-strong)', boxShadow: isConvertLeadEmployeeDropdownVisible ? '0 0 0 3px rgba(245, 158, 11, 0.18)' : 'none', borderRadius: '8px', padding: '10px 14px' }}
                    >
                      <span style={{ fontSize: '13.5px' }}>
                        {selectedEmployee
                          ? (employees.find(e => e.employee_number === selectedEmployee)?.full_name || 'Select Employee')
                          : 'Select Employee'}
                      </span>
                      <FiChevronDown style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', transition: 'transform 0.2s ease', transform: isConvertLeadEmployeeDropdownVisible ? 'rotate(180deg)' : 'none' }} />
                    </div>
                    {isConvertLeadEmployeeDropdownVisible && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, marginTop: '4px', backgroundColor: 'var(--bg-elevated, #ffffff)', border: '1px solid var(--border, #e5e7eb)', borderRadius: '8px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.12)', maxHeight: '200px', overflowY: 'auto', boxSizing: 'border-box' }}>
                        {employees.length === 0 ? (
                          <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>No employees found</div>
                        ) : employees.map(emp => (
                          <div
                            key={emp.employee_number}
                            onClick={() => { setSelectedEmployee(emp.employee_number); setConvertLeadEmployeeDropdownVisible(false); }}
                            style={{ padding: '10px 16px', cursor: 'pointer', fontSize: '13.5px', color: selectedEmployee === emp.employee_number ? 'var(--brand, #f59e0b)' : 'var(--text-primary)', fontWeight: selectedEmployee === emp.employee_number ? '600' : '500', backgroundColor: selectedEmployee === emp.employee_number ? 'var(--brand-light, #fef3c7)' : 'transparent', borderBottom: '1px solid var(--border-light, #f1f5f9)', transition: 'background-color 0.15s ease' }}
                            onMouseEnter={e => { if (selectedEmployee !== emp.employee_number) e.currentTarget.style.backgroundColor = 'var(--brand-light, #fef3c7)'; }}
                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = selectedEmployee === emp.employee_number ? 'var(--brand-light, #fef3c7)' : 'transparent'; }}
                          >
                            {emp.full_name || 'Name not available'}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="pmodal-footer">
                  <button className="pmodal-btn-cancel" onClick={closeModal}>Cancel</button>
                  <button className="pmodal-btn pmodal-btn-orange" onClick={() => { handleConvertEmployeeToLead(); closeModal(); }}>Convert to Lead</button>
                </div>
              </div>
            )}

            {activeModal === 'upload' && (
              <ProjectManagementInline onClose={closeModal} onProjectCreated={handleOrgProjectCreated} />
            )}

            {activeModal === 'assignProject' && (
              <div className="pmodal-section">
                <div className="pmodal-header">
                  <div className="pmodal-header-icon yellow-icon">📝</div>
                  <div className="pmodal-header-text">
                    <h2>Assign Project</h2>
                    <p>Assign a project to a manager and lead.</p>
                  </div>
                </div>
                <div className="pmodal-divider" />
                <div className="pmodal-body">
                  <AssignProjectInline onClose={closeModal} onProjectAssigned={handleOrgProjectCreated} />
                </div>
              </div>
            )}

            {activeModal === 'addOrg' && (
              <div className="pmodal-section">
                <div className="pmodal-header">
                  <div className="pmodal-header-icon blue-icon">🏢</div>
                  <div className="pmodal-header-text">
                    <h2>Add Employee to Organization</h2>
                    <p>Add a new employee to the organization database.</p>
                  </div>
                </div>
                <div className="pmodal-divider" />
                <div className="pmodal-body">
                  <AddEmployeeToOrgInline onClose={closeModal} onEmployeeAdded={handleOrgEmployeeAdded} />
                </div>
              </div>
            )}

            {activeModal === 'deleteOrg' && (
              <div className="pmodal-section">
                <div className="pmodal-header">
                  <div className="pmodal-header-icon red-icon">🗑️</div>
                  <div className="pmodal-header-text">
                    <h2>Delete Employee from Organization</h2>
                    <p>Delete an employee from the organization database.</p>
                  </div>
                </div>
                <div className="pmodal-divider" />
                <div className="pmodal-body">
                  <DeleteEmployeeFromOrgInline onClose={closeModal} onEmployeeDeleted={handleOrgEmployeeDeleted} />
                </div>
              </div>
            )}

            {activeModal === 'removeEmployeeProject' && (
              <RemoveEmployeeFromProjectInline
                onClose={closeModal}
                onEmployeeRemoved={() => {
                  fetchOrgData();
                }}
              />
            )}
            
          </div>
        </div>
      )}
      
      {/* Existing Lottie Success Dialog for Convert to Sub-Manager */}
      {isDialogVisible && (
        <div className="dialog-conversion">
          <Lottie animationData={successAnimation} loop={true} className="dialog-conversion-animation" />
          <p className="dialog-conversion-message">{dialogMessage}</p>
          <button className="conversion-close-button" onClick={() => setIsDialogVisible(false)}>Close</button>
        </div>
      )}
      
    </div>
  );
};

export default MESection;
